import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/userSlice";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import AddReactionOutlinedIcon from "@mui/icons-material/AddReactionOutlined";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { EmptyState, Spinner } from "../../../components/UI/States";
import {
  dmChannelId,
  fetchChannels,
  fetchChatDirectory,
  fetchMessages,
  fileToAttachment,
  markChannelRead,
  sendChatMessage,
  toggleReaction,
} from "./chatApi";

// Same reaction set the picker offers on every message. Any emoji the mobile
// app sends still renders — this only limits what web users can add.
const REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🎉"];

// Server page size (CHANNEL_MESSAGE_LIMIT in chatHandler.js) — a full page
// means there may be older history to load.
const PAGE_SIZE = 50;

const CHANNEL_POLL_MS = 5000;
const LIST_POLL_MS = 15000;

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const fmtDay = (iso) => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const previewText = (channel) => {
  const last = channel.lastMessage;
  if (!last) return "No messages yet";
  const who = (last.authorName || "").split(" ")[0];
  const body = last.text || (last.hasAttachment ? "📷 Photo" : "");
  return who ? `${who}: ${body}` : body;
};

const UnreadBadge = ({ count }) =>
  count > 0 ? (
    <span className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-sky-600 px-1.5 py-0.5 text-xs font-semibold text-white">
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

const Messages = () => {
  const user = useSelector(selectUser);
  const myEmail = (user?.email || "").toLowerCase();

  const [channels, setChannels] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [draftDm, setDraftDm] = useState(null);

  const [messages, setMessages] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);

  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);

  const [pickerFor, setPickerFor] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const [newDmOpen, setNewDmOpen] = useState(false);
  const [directory, setDirectory] = useState(null);
  const [dmFilter, setDmFilter] = useState("");

  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const activeIdRef = useRef(null);
  const messagesRef = useRef(null);
  const pollBusyRef = useRef(false);
  activeIdRef.current = activeId;
  messagesRef.current = messages;

  const scrollToBottom = useCallback((behavior = "auto") => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const isNearBottom = () => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  // ---------------------------------------------------------------- channels
  const loadChannels = useCallback(async () => {
    if (!myEmail) return;
    try {
      const data = await fetchChannels(myEmail);
      if (!Array.isArray(data)) return;
      setChannels(
        // The channel being read is never "unread", whatever the receipt
        // race says.
        data.map((c) =>
          c.channelId === activeIdRef.current ? { ...c, unread: 0 } : c
        )
      );
    } catch (err) {
      // Keep whatever list we had; the next poll retries.
      setChannels((prev) => prev || []);
    }
  }, [myEmail]);

  useEffect(() => {
    loadChannels();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") loadChannels();
    }, LIST_POLL_MS);
    return () => clearInterval(timer);
  }, [loadChannels]);

  const activeChannel = useMemo(
    () =>
      channels?.find((c) => c.channelId === activeId) ||
      (draftDm?.channelId === activeId ? draftDm : null),
    [channels, draftDm, activeId]
  );

  const markRead = useCallback(
    (channelId) => {
      if (!channelId || !myEmail) return;
      setChannels((prev) =>
        prev?.map((c) => (c.channelId === channelId ? { ...c, unread: 0 } : c))
      );
      markChannelRead({ channelId, email: myEmail }).catch(() => {});
    },
    [myEmail]
  );

  // ------------------------------------------------------- active channel IO
  useEffect(() => {
    if (!activeId || !myEmail) return undefined;
    let cancelled = false;
    setMessages(null);
    setError(null);
    setPickerFor(null);
    fetchMessages({ channelId: activeId, email: myEmail })
      .then(({ messages: initial }) => {
        if (cancelled) return;
        setMessages(initial);
        setHasMore(initial.length >= PAGE_SIZE);
        markRead(activeId);
        // Let the list render before jumping to the newest message.
        requestAnimationFrame(() => scrollToBottom("auto"));
      })
      .catch((err) => {
        if (cancelled) return;
        setMessages([]);
        setHasMore(false);
        setError(err.message || "Could not load this conversation.");
      });
    return () => {
      cancelled = true;
    };
  }, [activeId, myEmail, markRead, scrollToBottom]);

  useEffect(() => {
    if (!activeId || !myEmail) return undefined;
    const timer = setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      if (pollBusyRef.current) return;
      pollBusyRef.current = true;
      try {
        const latest = messagesRef.current;
        if (!latest) return;
        const after = latest.length
          ? latest[latest.length - 1].createdAt
          : new Date(0).toISOString();
        const { messages: fresh, reactionSync } = await fetchMessages({
          channelId: activeId,
          email: myEmail,
          after,
        });
        if (activeIdRef.current !== activeId) return;
        const stick = isNearBottom();
        let gotNew = false;
        setMessages((prev) => {
          if (!prev) return prev;
          const known = new Set(prev.map((m) => m._id));
          const additions = (fresh || []).filter((m) => !known.has(m._id));
          gotNew = additions.length > 0;
          let next = additions.length ? [...prev, ...additions] : prev.slice();
          if (reactionSync?.length) {
            const byId = new Map(reactionSync.map((r) => [r._id, r.reactions]));
            next = next.map((m) =>
              byId.has(m._id) ? { ...m, reactions: byId.get(m._id) } : m
            );
          }
          return next;
        });
        if (gotNew) {
          markRead(activeId);
          if (stick) requestAnimationFrame(() => scrollToBottom("smooth"));
        }
      } catch (err) {
        // transient poll failure — next tick retries
      } finally {
        pollBusyRef.current = false;
      }
    }, CHANNEL_POLL_MS);
    return () => clearInterval(timer);
  }, [activeId, myEmail, markRead, scrollToBottom]);

  const loadOlder = async () => {
    const first = messages?.[0];
    if (!first || !activeId) return;
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    try {
      const { messages: older } = await fetchMessages({
        channelId: activeId,
        email: myEmail,
        before: first.createdAt,
      });
      setMessages((prev) => (prev ? [...older, ...prev] : older));
      setHasMore(older.length >= PAGE_SIZE);
      // Keep the viewport anchored on the message the user was reading.
      requestAnimationFrame(() => {
        if (el) el.scrollTop += el.scrollHeight - prevHeight;
      });
    } catch (err) {
      setError(err.message || "Could not load older messages.");
    }
  };

  // ------------------------------------------------------------------- send
  const handleSend = async () => {
    const trimmed = text.trim();
    if ((!trimmed && !attachment) || sending || !activeChannel) return;
    setSending(true);
    setError(null);
    try {
      const sent = await sendChatMessage({
        channelId: activeId,
        email: myEmail,
        name: user?.name || myEmail,
        text: trimmed,
        attachment,
      });
      setText("");
      setAttachment(null);
      setMessages((prev) => (prev ? [...prev, sent] : [sent]));
      // A draft DM becomes a real channel on its first message.
      if (draftDm?.channelId === activeId) loadChannels();
      requestAnimationFrame(() => scrollToBottom("smooth"));
    } catch (err) {
      setError(err.message || "Message didn't send — try again.");
    } finally {
      setSending(false);
    }
  };

  const handleAttachFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Only images can be attached.");
      return;
    }
    try {
      setAttachment(await fileToAttachment(file));
    } catch (err) {
      setError(err.message || "Could not read that image.");
    }
  };

  const handleReaction = async (message, emoji) => {
    setPickerFor(null);
    try {
      const { reactions } = await toggleReaction({
        messageId: message._id,
        email: myEmail,
        name: user?.name || myEmail,
        emoji,
      });
      setMessages((prev) =>
        prev?.map((m) => (m._id === message._id ? { ...m, reactions } : m))
      );
    } catch (err) {
      // Reaction toggles are low-stakes; the next poll resyncs them anyway.
    }
  };

  // ----------------------------------------------------------------- new DM
  const openNewDm = () => {
    setNewDmOpen(true);
    setDmFilter("");
    if (!directory && myEmail) {
      fetchChatDirectory(myEmail)
        .then((list) => setDirectory(Array.isArray(list) ? list : []))
        .catch(() => setDirectory([]));
    }
  };

  const startDm = (person) => {
    const channelId = dmChannelId(myEmail, person.email);
    setNewDmOpen(false);
    if (!channels?.some((c) => c.channelId === channelId)) {
      setDraftDm({
        channelId,
        type: "dm",
        name: person.name,
        otherEmail: person.email,
        lastMessage: null,
        unread: 0,
      });
    }
    setActiveId(channelId);
  };

  // ----------------------------------------------------------------- render
  const groupChannels = (channels || []).filter((c) => c.type !== "dm");
  const dmChannels = (channels || [])
    .filter((c) => c.type === "dm")
    .sort(
      (a, b) =>
        new Date(b.lastMessage?.createdAt || 0) -
        new Date(a.lastMessage?.createdAt || 0)
    );
  const draftVisible =
    draftDm && !dmChannels.some((c) => c.channelId === draftDm.channelId);

  const filteredDirectory = (directory || []).filter((person) => {
    const q = dmFilter.trim().toLowerCase();
    if (!q) return true;
    return `${person.name} ${person.email} ${person.branch} ${person.title}`
      .toLowerCase()
      .includes(q);
  });

  const channelRow = (channel, icon) => {
    const isActive = channel.channelId === activeId;
    return (
      <button
        key={channel.channelId}
        onClick={() => setActiveId(channel.channelId)}
        type="button"
        aria-current={isActive ? "true" : undefined}
        className={`flex min-h-[3.25rem] w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors ${
          isActive
            ? "bg-sky-500/15"
            : "hover:bg-[color:var(--app-hover)]"
        }`}
      >
        {icon}
        <span className="min-w-0 flex-1">
          <span className="flex items-center">
            <span
              className={`truncate text-sm ${
                channel.unread > 0 ? "font-bold" : "font-medium"
              } ${isActive ? "text-sky-800 dark:text-sky-200" : ""}`}
            >
              {channel.name}
            </span>
            <UnreadBadge count={channel.unread} />
          </span>
          <span className="app-muted block truncate text-xs">
            {previewText(channel)}
          </span>
        </span>
        {channel.lastMessage && (
          <span className="app-muted shrink-0 self-start pt-0.5 text-[11px] tabular-nums">
            {fmtDay(channel.lastMessage.createdAt) === "Today"
              ? fmtTime(channel.lastMessage.createdAt)
              : fmtDay(channel.lastMessage.createdAt)}
          </span>
        )}
      </button>
    );
  };

  const dmAvatar = (name) => (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-500/25 to-violet-500/25 text-sm font-semibold text-sky-800 dark:text-sky-200">
      {(name || "?")
        .split(" ")
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase()}
    </span>
  );

  const groupIcon = (type) =>
    type === "all" ? (
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600/15">
        <ForumRoundedIcon
          className="text-emerald-700 dark:text-emerald-300"
          sx={{ fontSize: 20 }}
        />
      </span>
    ) : (
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-600/15">
        <StorefrontRoundedIcon
          className="text-amber-700 dark:text-amber-300"
          sx={{ fontSize: 20 }}
        />
      </span>
    );

  const renderMessage = (message, index) => {
    const prev = messages[index - 1];
    const own = (message.authorEmail || "").toLowerCase() === myEmail;
    const newDay =
      !prev ||
      new Date(prev.createdAt).toDateString() !==
        new Date(message.createdAt).toDateString();
    const grouped =
      !newDay &&
      prev &&
      prev.authorEmail === message.authorEmail &&
      new Date(message.createdAt) - new Date(prev.createdAt) < 5 * 60 * 1000;

    // Group reactions into chips: one per emoji with count + who.
    const chips = [];
    for (const reaction of message.reactions || []) {
      const chip = chips.find((c) => c.emoji === reaction.emoji);
      if (chip) {
        chip.count += 1;
        chip.names.push(reaction.name || reaction.email);
        chip.mine = chip.mine || (reaction.email || "").toLowerCase() === myEmail;
      } else {
        chips.push({
          emoji: reaction.emoji,
          count: 1,
          names: [reaction.name || reaction.email],
          mine: (reaction.email || "").toLowerCase() === myEmail,
        });
      }
    }

    return (
      <React.Fragment key={message._id}>
        {newDay && (
          <div className="my-4 flex items-center gap-3">
            <div className="app-divider flex-1 border-t" />
            <span className="app-muted text-xs font-medium">
              {fmtDay(message.createdAt)}
            </span>
            <div className="app-divider flex-1 border-t" />
          </div>
        )}
        <div
          className={`group relative flex ${own ? "justify-end" : "justify-start"} ${
            grouped ? "mt-0.5" : "mt-3"
          }`}
        >
          <div className={`max-w-[85%] md:max-w-[70%] ${own ? "text-right" : ""}`}>
            {!grouped && (
              <p className={`mb-0.5 text-xs ${own ? "app-muted" : ""}`}>
                {!own && (
                  <span className="font-semibold">{message.authorName}</span>
                )}
                <span className="app-muted">
                  {!own && " · "}
                  {fmtTime(message.createdAt)}
                </span>
              </p>
            )}
            <div className="relative inline-block text-left">
              <div
                className={`rounded-[1.15rem] px-3.5 py-2 shadow-sm ${
                  own
                    ? "rounded-br-md bg-sky-600 text-white"
                    : "app-surface rounded-bl-md border"
                }`}
                title={new Date(message.createdAt).toLocaleString()}
              >
                {message.attachment?.data && (
                  <button
                    type="button"
                    onClick={() => setLightboxSrc(message.attachment.data)}
                    aria-label="View attachment full size"
                    className="mb-1 block cursor-zoom-in"
                  >
                    <img
                      src={message.attachment.data}
                      alt="Chat attachment"
                      className="max-h-64 max-w-full rounded-xl"
                    />
                  </button>
                )}
                {message.text && (
                  <p className="whitespace-pre-wrap break-words text-sm">
                    {message.text}
                  </p>
                )}
              </div>
              {/* Add-reaction affordance, revealed on hover */}
              <button
                onClick={() =>
                  setPickerFor(pickerFor === message._id ? null : message._id)
                }
                type="button"
                className={`absolute top-1/2 -translate-y-1/2 rounded-full p-1 opacity-0 transition hover:bg-[color:var(--app-hover)] focus:opacity-100 group-hover:opacity-100 ${
                  own ? "-left-8" : "-right-8"
                }`}
                aria-label="Add reaction"
              >
                <AddReactionOutlinedIcon
                  className="app-muted"
                  sx={{ fontSize: 18 }}
                />
              </button>
              {pickerFor === message._id && (
                <div
                  className={`app-glass app-glass-rim absolute z-10 flex gap-0.5 rounded-full border px-1.5 py-1 ${
                    own ? "right-0" : "left-0"
                  } -top-11`}
                >
                  {REACTION_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => handleReaction(message, emoji)}
                      aria-label={`React with ${emoji}`}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-lg transition hover:scale-125 hover:bg-[color:var(--app-hover)]"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {chips.length > 0 && (
              <div
                className={`mt-1 flex flex-wrap gap-1 ${
                  own ? "justify-end" : ""
                }`}
              >
                {chips.map((chip) => (
                  <button
                    key={chip.emoji}
                    type="button"
                    onClick={() => handleReaction(message, chip.emoji)}
                    title={chip.names.join(", ")}
                    className={`rounded-full border px-2 py-0.5 text-xs tabular-nums transition-colors ${
                      chip.mine
                        ? "border-sky-500/60 bg-sky-500/15 text-sky-800 dark:text-sky-200"
                        : "app-surface hover:bg-[color:var(--app-hover)]"
                    }`}
                  >
                    {chip.emoji} {chip.count}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  };

  return (
    <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
      <div className="mx-auto flex h-[calc(100vh-150px)] w-full max-w-[90rem] flex-col px-4 py-4">
        <div className="app-card flex min-h-0 flex-1 overflow-hidden rounded-[1.5rem] border">
          {/* -------------------------------------------- channel list pane */}
          <div
            className={`app-divider w-full flex-col border-r md:flex md:w-80 lg:w-[22rem] ${
              activeId ? "hidden" : "flex"
            }`}
          >
            <div className="app-divider flex items-center justify-between gap-3 border-b px-4 py-3.5">
              <div className="flex min-w-0 items-center gap-3">
                <span className="app-icon-tile h-10 w-10 rounded-xl bg-violet-500/15 text-violet-600 dark:text-violet-300">
                  <ForumRoundedIcon sx={{ fontSize: 20 }} />
                </span>
                <div className="min-w-0">
                  <p className="app-eyebrow">Everyday Tools</p>
                  <h1 className="text-lg font-bold leading-tight tracking-tight">
                    Messages
                  </h1>
                </div>
              </div>
              <button
                type="button"
                onClick={openNewDm}
                className="app-btn app-btn-primary app-btn-sm"
              >
                <AddCommentOutlinedIcon sx={{ fontSize: 16 }} />
                New
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
              {!channels && (
                <div className="app-muted flex items-center gap-2 p-3 text-sm">
                  <Spinner size={16} />
                  Loading…
                </div>
              )}
              {channels && (
                <>
                  <p className="app-eyebrow px-3 pb-1 pt-1">Channels</p>
                  {groupChannels.map((channel) =>
                    channelRow(channel, groupIcon(channel.type))
                  )}
                  <p className="app-eyebrow px-3 pb-1 pt-4">Direct messages</p>
                  {draftVisible && channelRow(draftDm, dmAvatar(draftDm.name))}
                  {dmChannels.map((channel) =>
                    channelRow(channel, dmAvatar(channel.name))
                  )}
                  {dmChannels.length === 0 && !draftVisible && (
                    <p className="app-muted px-3 py-2 text-sm">
                      No direct messages yet — click New to start one.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ------------------------------------------------- thread pane */}
          <div
            className={`min-w-0 flex-1 flex-col md:flex ${
              activeId ? "flex" : "hidden"
            }`}
          >
            {!activeChannel ? (
              <div className="flex flex-1 items-center justify-center p-8">
                <EmptyState
                  icon={ForumRoundedIcon}
                  title="Pick a conversation"
                  description="Company-wide and your branch channel are on the left, or start a direct message with New. Everything here syncs with chat in the mobile app."
                />
              </div>
            ) : (
              <>
                <div className="app-divider flex items-center gap-3 border-b px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setActiveId(null)}
                    className="app-icon-btn -ml-2 md:hidden"
                    aria-label="Back to conversations"
                  >
                    <ArrowBackIosNewRoundedIcon sx={{ fontSize: 18 }} />
                  </button>
                  {activeChannel.type === "dm"
                    ? dmAvatar(activeChannel.name)
                    : groupIcon(activeChannel.type)}
                  <div className="min-w-0">
                    <p className="truncate font-semibold tracking-tight">
                      {activeChannel.name}
                    </p>
                    <p className="app-muted truncate text-xs">
                      {activeChannel.type === "all" &&
                        "Everyone at Big C Lumber"}
                      {activeChannel.type === "branch" &&
                        "Everyone at your branch"}
                      {activeChannel.type === "dm" && activeChannel.otherEmail}
                    </p>
                  </div>
                </div>

                <div
                  ref={scrollRef}
                  className="min-h-0 flex-1 overflow-y-auto px-4 py-3"
                >
                  {messages === null && (
                    <div className="app-muted flex items-center justify-center gap-2 py-6 text-sm">
                      <Spinner size={16} />
                      Loading messages…
                    </div>
                  )}
                  {messages && hasMore && (
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={loadOlder}
                        className="app-btn app-btn-secondary app-btn-sm my-1"
                      >
                        Load older messages
                      </button>
                    </div>
                  )}
                  {messages && messages.length === 0 && (
                    <p className="app-muted py-6 text-center text-sm">
                      No messages yet — say hi!
                    </p>
                  )}
                  {messages?.map(renderMessage)}
                </div>

                {error && (
                  <p
                    role="alert"
                    className="mx-3 mb-2 rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
                  >
                    {error}
                  </p>
                )}
                {attachment && (
                  <div className="flex items-center gap-2 px-4 pb-2">
                    <img
                      src={attachment.data}
                      alt="Ready to send"
                      className="app-divider h-14 w-14 rounded-xl border object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setAttachment(null)}
                      className="app-icon-btn h-9 w-9"
                      aria-label="Remove attachment"
                    >
                      <CloseRoundedIcon sx={{ fontSize: 18 }} />
                    </button>
                  </div>
                )}
                <div className="app-divider flex items-end gap-2 border-t px-3 py-2.5">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAttachFile}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="app-icon-btn shrink-0"
                    aria-label="Attach an image"
                  >
                    <ImageOutlinedIcon sx={{ fontSize: 22 }} />
                  </button>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    rows={Math.min(4, text.split("\n").length)}
                    placeholder={`Message ${activeChannel.name}…`}
                    aria-label={`Message ${activeChannel.name}`}
                    className="app-input min-h-[2.5rem] min-w-0 flex-1 resize-none rounded-[1.25rem] border px-4 py-2 text-sm leading-6 outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={sending || (!text.trim() && !attachment)}
                    className="app-btn app-btn-primary h-10 w-10 shrink-0 p-0"
                    aria-label="Send message"
                  >
                    <SendRoundedIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* --------------------------------------------------- new DM modal */}
      {newDmOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-[color:var(--app-scrim)] p-4"
          onClick={() => setNewDmOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-dm-title"
            className="app-glass app-glass-rim flex max-h-[70vh] w-full max-w-md flex-col rounded-3xl border p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 id="new-dm-title" className="text-lg font-semibold tracking-tight">
                New direct message
              </h2>
              <button
                type="button"
                onClick={() => setNewDmOpen(false)}
                className="app-icon-btn -mr-2 h-9 w-9"
                aria-label="Close"
              >
                <CloseRoundedIcon sx={{ fontSize: 20 }} />
              </button>
            </div>
            <input
              autoFocus
              value={dmFilter}
              onChange={(e) => setDmFilter(e.target.value)}
              placeholder="Search by name, branch, or email…"
              aria-label="Search people"
              className="app-input mb-3 rounded-xl border px-3 py-2.5 text-sm outline-none"
            />
            <div className="min-h-0 flex-1 overflow-y-auto">
              {!directory && (
                <div className="app-muted flex items-center gap-2 p-2 text-sm">
                  <Spinner size={16} />
                  Loading people…
                </div>
              )}
              {directory && filteredDirectory.length === 0 && (
                <p className="app-muted p-2 text-sm">No one matches that.</p>
              )}
              {filteredDirectory.map((person) => (
                <button
                  key={person.email}
                  type="button"
                  onClick={() => startDm(person)}
                  className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-[color:var(--app-hover)]"
                >
                  {dmAvatar(person.name)}
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">
                      {person.name}
                    </span>
                    <span className="app-muted block truncate text-xs">
                      {[person.title, person.branch].filter(Boolean).join(" · ") ||
                        person.email}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------- lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-black/80 p-4"
          onClick={() => setLightboxSrc(null)}
        >
          <img
            src={lightboxSrc}
            alt="Attachment"
            className="max-h-full max-w-full rounded-2xl shadow-2xl"
          />
        </div>
      )}
    </div>
  );
};

export default Messages;
